'use client';

import React, { useState, useEffect } from 'react';
import ServiceHero from './ServiceHero';
import ServiceCard from './ServiceCard';
import CapabilityMatrix from './CapabilityMatrix';
import TechnologyStack from './TechnologyStack';
import ServiceComparison from './ServiceComparison';
import CaseStudyIntegration from './CaseStudyIntegration';
import ClientTestimonials from './ClientTestimonials';
import ROICalculator from './ROICalculator';
import CTASection from './CTASection';

interface ServiceFeature {
  title: string;
  description: string;
}

interface Service {
  id: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  features: ServiceFeature[];
  technologies: string[];
  caseStudyCount: number;
}

interface Capability {
  name: string;
  level: 'Expert' | 'Advanced' | 'Proficient';
  description: string;
}

interface ServiceCategory {
  category: string;
  capabilities: Capability[];
}

interface Technology {
  name: string;
  logo: string;
  logoAlt: string;
  category: string;
  integrations: string[];
  useCases: string[];
}

interface ServiceFeatureComparison {
  feature: string;
  basic: boolean;
  professional: boolean;
  enterprise: boolean;
}

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: string[];
  image: string;
  imageAlt: string;
  serviceCategory: string;
}

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  image: string;
  imageAlt: string;
  quote: string;
  service: string;
  rating: number;
}

const ServicesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
  {
    id: "digital-transformation",
    title: "Digital Transformation",
    description: "End-to-end modernization of legacy systems and processes",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19a863b67-1764664295014.png",
    imageAlt: "Modern office workspace with multiple computer monitors displaying data analytics dashboards and digital transformation metrics",
    features: [
    { title: "Legacy System Modernization", description: "Migrate from outdated infrastructure to cloud-native solutions" },
    { title: "Process Automation", description: "Streamline workflows with intelligent automation" },
    { title: "Digital Strategy Consulting", description: "Roadmap development for enterprise-wide transformation" }],

    technologies: ["Azure", "AWS", "Kubernetes", "Terraform", "Docker"],
    caseStudyCount: 12
  },
  {
    id: "cloud-solutions",
    title: "Cloud Solutions",
    description: "Scalable cloud infrastructure and migration services",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cef61144-1767329840097.png",
    imageAlt: "Abstract visualization of cloud computing network with interconnected nodes and data streams in blue and purple colors",
    features: [
    { title: "Cloud Migration", description: "Seamless transition to AWS, Azure, or GCP" },
    { title: "Multi-Cloud Architecture", description: "Optimize costs with hybrid cloud strategies" },
    { title: "Cloud Security", description: "Enterprise-grade security and compliance" }],

    technologies: ["AWS", "Azure", "GCP", "CloudFormation", "Ansible"],
    caseStudyCount: 18
  },
  {
    id: "ai-ml-solutions",
    title: "AI & Machine Learning",
    description: "Intelligent automation and predictive analytics",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c65d4e31-1767089543266.png",
    imageAlt: "Futuristic artificial intelligence visualization with neural network patterns and glowing blue circuits representing machine learning algorithms",
    features: [
    { title: "Predictive Analytics", description: "Data-driven insights for strategic decisions" },
    { title: "Natural Language Processing", description: "Advanced text and speech analysis" },
    { title: "Computer Vision", description: "Image recognition and visual intelligence" }],

    technologies: ["TensorFlow", "PyTorch", "Scikit-learn", "OpenAI", "Hugging Face"],
    caseStudyCount: 9
  },
  {
    id: "data-analytics",
    title: "Data Analytics & BI",
    description: "Transform raw data into actionable business intelligence",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_162e1bb75-1764659449969.png",
    imageAlt: "Business intelligence dashboard on laptop screen showing colorful charts, graphs and data visualization with coffee cup beside",
    features: [
    { title: "Data Warehousing", description: "Centralized data storage and management" },
    { title: "Real-time Analytics", description: "Live dashboards and reporting" },
    { title: "Business Intelligence", description: "Self-service analytics platforms" }],

    technologies: ["Snowflake", "Tableau", "Power BI", "Apache Spark", "Databricks"],
    caseStudyCount: 15
  },
  {
    id: "enterprise-architecture",
    title: "Enterprise Architecture",
    description: "Strategic technology planning and system design",
    image: "https://images.unsplash.com/photo-1621867555827-e9e145ab016b",
    imageAlt: "Modern glass office building exterior with geometric architecture reflecting blue sky symbolizing enterprise structure",
    features: [
    { title: "Architecture Assessment", description: "Evaluate current state and identify gaps" },
    { title: "Solution Design", description: "Scalable and maintainable system blueprints" },
    { title: "Technology Roadmap", description: "Strategic planning for technology evolution" }],

    technologies: ["TOGAF", "ArchiMate", "Microservices", "API Gateway", "Service Mesh"],
    caseStudyCount: 10
  },
  // {
  //   id: "cybersecurity",
  //   title: "Cybersecurity Services",
  //   description: "Comprehensive security solutions and compliance",
  //   image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b358e0c3-1764672594754.png",
  //   imageAlt: "Digital padlock icon with binary code background representing cybersecurity protection and data encryption",
  //   features: [
  //   { title: "Security Assessment", description: "Vulnerability scanning and penetration testing" },
  //   { title: "Compliance Management", description: "SOC 2, ISO 27001, GDPR compliance" },
  //   { title: "Incident Response", description: "24/7 security monitoring and response" }],

  //   technologies: ["Splunk", "CrowdStrike", "Palo Alto", "Okta", "HashiCorp Vault"],
  //   caseStudyCount: 14
  // }
  ];


  const capabilityData: ServiceCategory[] = [
  {
    category: "Cloud & Infrastructure",
    capabilities: [
    { name: "AWS Solutions Architecture", level: "Expert", description: "Design and implement scalable AWS infrastructure" },
    { name: "Azure Enterprise Integration", level: "Expert", description: "Seamless Azure cloud migration and management" },
    { name: "Kubernetes Orchestration", level: "Advanced", description: "Container orchestration at enterprise scale" },
    { name: "Infrastructure as Code", level: "Expert", description: "Terraform and CloudFormation expertise" }]

  },
  {
    category: "Data & Analytics",
    capabilities: [
    { name: "Big Data Processing", level: "Expert", description: "Apache Spark and Hadoop ecosystem" },
    { name: "Data Warehousing", level: "Advanced", description: "Snowflake and Redshift implementations" },
    { name: "Real-time Analytics", level: "Advanced", description: "Stream processing with Kafka and Flink" },
    { name: "Business Intelligence", level: "Expert", description: "Tableau and Power BI dashboard development" }]

  },
  {
    category: "AI & Machine Learning",
    capabilities: [
    { name: "Deep Learning Models", level: "Advanced", description: "TensorFlow and PyTorch implementations" },
    { name: "Natural Language Processing", level: "Expert", description: "Advanced NLP and text analytics" },
    { name: "Computer Vision", level: "Advanced", description: "Image recognition and object detection" },
    { name: "MLOps", level: "Proficient", description: "ML model deployment and monitoring" }]

  },
  {
    category: "Security & Compliance",
    capabilities: [
    { name: "Security Architecture", level: "Expert", description: "Zero-trust security design" },
    { name: "Compliance Automation", level: "Advanced", description: "SOC 2 and ISO 27001 compliance" },
    { name: "Identity Management", level: "Expert", description: "SSO and IAM solutions" },
    { name: "Threat Detection", level: "Advanced", description: "SIEM and security monitoring" }]

  }];


  const technologies: Technology[] = [
  {
    name: "Amazon Web Services",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_14de2e03d-1764664247285.png",
    logoAlt: "AWS cloud services logo with orange and white color scheme",
    category: "Cloud Platform",
    integrations: ["Azure", "GCP", "Kubernetes", "Terraform"],
    useCases: ["Cloud Migration", "Serverless Computing", "Data Lakes", "IoT Solutions"]
  },
  {
    name: "Microsoft Azure",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_144ac7294-1764641706531.png",
    logoAlt: "Microsoft Azure cloud platform logo in blue gradient",
    category: "Cloud Platform",
    integrations: ["AWS", "Office 365", "Active Directory", "Power Platform"],
    useCases: ["Enterprise Integration", "Hybrid Cloud", "AI Services", "DevOps"]
  },
  {
    name: "Kubernetes",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1dba6279c-1764655536513.png",
    logoAlt: "Kubernetes container orchestration platform logo with blue hexagonal design",
    category: "Container Orchestration",
    integrations: ["Docker", "Helm", "Istio", "Prometheus"],
    useCases: ["Microservices", "Auto-scaling", "Multi-cloud Deployment", "CI/CD"]
  },
  {
    name: "TensorFlow",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_10fc1cd0d-1764675851398.png",
    logoAlt: "TensorFlow machine learning framework logo with orange geometric design",
    category: "Machine Learning",
    integrations: ["Keras", "PyTorch", "Jupyter", "MLflow"],
    useCases: ["Deep Learning", "Neural Networks", "Image Recognition", "NLP"]
  },
  {
    name: "Snowflake",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7b1f3e6-1767581888835.png",
    logoAlt: "Snowflake data warehouse platform logo with blue snowflake icon",
    category: "Data Warehouse",
    integrations: ["Tableau", "Power BI", "dbt", "Apache Airflow"],
    useCases: ["Data Warehousing", "Data Sharing", "Analytics", "Data Lake"]
  },
  {
    name: "Terraform",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fdd6b05d-1767694522538.png",
    logoAlt: "Terraform infrastructure as code tool logo with purple geometric pattern",
    category: "Infrastructure as Code",
    integrations: ["AWS", "Azure", "GCP", "Ansible"],
    useCases: ["Cloud Provisioning", "Multi-cloud Management", "Infrastructure Automation", "GitOps"]
  },
  {
    name: "Docker",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1af9eed97-1764675856157.png",
    logoAlt: "Docker containerization platform logo with blue whale carrying containers",
    category: "Containerization",
    integrations: ["Kubernetes", "Jenkins", "GitLab", "Harbor"],
    useCases: ["Application Packaging", "Microservices", "Development Environments", "CI/CD"]
  },
  {
    name: "Apache Spark",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_14b8ed3f1-1765279453039.png",
    logoAlt: "Apache Spark big data processing framework logo with orange lightning bolt",
    category: "Big Data",
    integrations: ["Hadoop", "Kafka", "Databricks", "Delta Lake"],
    useCases: ["Big Data Processing", "Real-time Analytics", "Machine Learning", "ETL"]
  }];


  const comparisonFeatures: ServiceFeatureComparison[] = [
  { feature: "Cloud Infrastructure Setup", basic: true, professional: true, enterprise: true },
  { feature: "24/7 Technical Support", basic: false, professional: true, enterprise: true },
  { feature: "Custom Integration Development", basic: false, professional: true, enterprise: true },
  { feature: "Dedicated Solutions Architect", basic: false, professional: false, enterprise: true },
  { feature: "Advanced Security Features", basic: false, professional: true, enterprise: true },
  { feature: "Multi-region Deployment", basic: false, professional: false, enterprise: true },
  { feature: "Disaster Recovery Planning", basic: false, professional: true, enterprise: true },
  { feature: "Performance Optimization", basic: true, professional: true, enterprise: true },
  { feature: "Compliance Certification Support", basic: false, professional: false, enterprise: true },
  { feature: "Custom Training Programs", basic: false, professional: false, enterprise: true },
  { feature: "API Development & Management", basic: false, professional: true, enterprise: true },
  { feature: "Data Migration Services", basic: true, professional: true, enterprise: true }];


  const caseStudies: CaseStudy[] = [
  {
    id: "cs-001",
    title: "Global Retail Chain Cloud Migration",
    client: "RetailCorp International",
    industry: "Retail & E-commerce",
    challenge: "Legacy on-premise infrastructure causing scalability issues during peak shopping seasons, resulting in lost revenue and poor customer experience.",
    solution: "Implemented multi-region AWS architecture with auto-scaling, migrated 200+ applications to cloud-native services, and established CI/CD pipelines for rapid deployment.",
    results: [
    "99.99% uptime during Black Friday sales",
    "60% reduction in infrastructure costs",
    "3x faster deployment cycles",
    "Handled 500% traffic spike without performance degradation"],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12a638202-1766959266777.png",
    imageAlt: "Modern retail store interior with customers shopping and digital displays showing product information",
    serviceCategory: "Cloud Solutions"
  },
  {
    id: "cs-002",
    title: "Healthcare AI Diagnostic Platform",
    client: "MediTech Solutions",
    industry: "Healthcare & Life Sciences",
    challenge: "Manual diagnostic processes taking 48+ hours, leading to delayed treatment decisions and increased healthcare costs for patients.",
    solution: "Developed AI-powered diagnostic platform using TensorFlow and computer vision to analyze medical imaging, integrated with existing EMR systems for seamless workflow.",
    results: [
    "Reduced diagnosis time from 48 hours to 15 minutes",
    "94% accuracy in early disease detection",
    "Processed 10,000+ patient cases monthly",
    "$2.5M annual cost savings in diagnostic operations"],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d85f07f-1764675861970.png",
    imageAlt: "Medical professional in white coat analyzing patient data on digital tablet with healthcare technology interface",
    serviceCategory: "AI & Machine Learning"
  },
  {
    id: "cs-003",
    title: "Financial Services Data Analytics",
    client: "FinanceFirst Bank",
    industry: "Banking & Financial Services",
    challenge: "Fragmented data across 50+ systems preventing real-time insights, regulatory compliance issues, and inability to detect fraudulent transactions quickly.",
    solution: "Built enterprise data warehouse on Snowflake, implemented real-time analytics with Apache Kafka, and created executive dashboards using Tableau for data-driven decision making.",
    results: [
    "Unified data from 50+ disparate systems",
    "Real-time fraud detection reducing losses by 75%",
    "Compliance reporting time reduced from weeks to hours",
    "Executive decision-making speed improved by 10x"],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1370e02cd-1766487882870.png",
    imageAlt: "Financial analyst reviewing stock market data and analytics charts on multiple computer monitors in modern office",
    serviceCategory: "Data Analytics & BI"
  },
  {
    id: "cs-004",
    title: "Manufacturing IoT & Automation",
    client: "IndustrialTech Manufacturing",
    industry: "Manufacturing & Industrial",
    challenge: "Equipment downtime costing $50K per hour, manual quality control missing defects, and lack of predictive maintenance capabilities.",
    solution: "Deployed IoT sensors across production lines, implemented predictive maintenance using machine learning, and automated quality control with computer vision systems.",
    results: [
    "85% reduction in unplanned downtime",
    "99.7% product quality rate (up from 94%)",
    "$8M annual savings from predictive maintenance",
    "Real-time production monitoring across 12 facilities"],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11bc6f378-1767163609916.png",
    imageAlt: "Industrial manufacturing facility with robotic arms and automated assembly line producing high-tech components",
    serviceCategory: "Digital Transformation"
  }];


  const testimonials: Testimonial[] = [
  {
    id: "t-001",
    name: "Sarah Mitchell",
    role: "Chief Technology Officer",
    company: "RetailCorp International",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c0007e95-1763295050768.png",
    imageAlt: "Professional woman with short brown hair in navy business suit smiling confidently in modern office setting",
    quote: "SarviTEQ transformed our entire infrastructure in just 6 months. Their expertise in cloud architecture and commitment to our success was evident from day one. The ROI exceeded our expectations.",
    service: "Cloud Solutions",
    rating: 5
  },
  {
    id: "t-002",
    name: "Dr. James Chen",
    role: "VP of Technology & Innovation",
    company: "MediTech Solutions",
    image: "https://images.unsplash.com/photo-1673865640533-f77087affdd8",
    imageAlt: "Asian male executive in white medical coat with stethoscope standing in hospital corridor with arms crossed",
    quote: "The AI diagnostic platform developed by SarviTEQ has revolutionized our patient care. Their deep understanding of both healthcare and cutting-edge AI technology made them the perfect partner.",
    service: "AI & Machine Learning",
    rating: 5
  },
  {
    id: "t-003",
    name: "Michael Rodriguez",
    role: "Chief Information Officer",
    company: "FinanceFirst Bank",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1623a7667-1763293855381.png",
    imageAlt: "Hispanic businessman in dark suit with beard looking professional in corporate banking environment",
    quote: "Working with SarviTEQ on our data analytics transformation was a game-changer. They delivered a solution that not only met our compliance requirements but also gave us competitive advantages we didn't anticipate.",
    service: "Data Analytics & BI",
    rating: 5
  },
  {
    id: "t-004",
    name: "Emily Thompson",
    role: "Director of IT Operations",
    company: "IndustrialTech Manufacturing",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a90aacde-1763301697203.png",
    imageAlt: "Professional woman with blonde hair in industrial safety vest and hard hat at manufacturing facility",
    quote: "The predictive maintenance system has saved us millions. SarviTEQ's team understood our manufacturing challenges and delivered a solution that exceeded all expectations. Truly a strategic partner.",
    service: "Digital Transformation",
    rating: 5
  }];


  const handleExploreService = (serviceId: string) => {
    if (isHydrated) {
      console.log(`Exploring service: ${serviceId}`);
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/services/components/ServicesInteractive.tsx:424:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="424" data-component-end-line="438" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:425:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="425" data-component-end-line="437" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-32%20pb-20%20px-4%22%7D" className="pt-32 pb-20 px-4">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:426:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="426" data-component-end-line="436" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:427:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="427" data-component-end-line="435" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
              <div data-component-id="src/app/services/components/ServicesInteractive.tsx:428:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="428" data-component-end-line="428" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20w-3%2F4%20mx-auto%22%7D" className="h-12 bg-muted rounded w-3/4 mx-auto" />
              <div data-component-id="src/app/services/components/ServicesInteractive.tsx:429:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="429" data-component-end-line="429" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20w-1%2F2%20mx-auto%22%7D" className="h-6 bg-muted rounded w-1/2 mx-auto" />
              <div data-component-id="src/app/services/components/ServicesInteractive.tsx:430:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="430" data-component-end-line="434" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) =>
                <div data-component-id="src/app/services/components/ServicesInteractive.tsx:432:16" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="432" data-component-end-line="432" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" key={i} className="h-96 bg-muted rounded-lg" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/services/components/ServicesInteractive.tsx:443:4" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="443" data-component-end-line="482" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <ServiceHero data-component-id="src/app/services/components/ServicesInteractive.tsx:444:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="444" data-component-end-line="444" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceHero" data-component-content="%7B%22elementName%22%3A%22ServiceHero%22%7D" />
      
      <section data-component-id="src/app/services/components/ServicesInteractive.tsx:446:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="446" data-component-end-line="473" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20bg-background%22%7D" className="py-16 bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:447:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="447" data-component-end-line="472" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:448:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="448" data-component-end-line="455" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h2 data-component-id="src/app/services/components/ServicesInteractive.tsx:449:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="449" data-component-end-line="451" data-component-file="ServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20sm%3Atext-4xl%20font-heading%20font-bold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Our%20Service%20Portfolio%22%7D" className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
              Our Service Portfolio
            </h2>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:452:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="452" data-component-end-line="454" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-text-secondary%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Comprehensive%20technology%20solutions%20designed%20to%20drive%20digital%20transformation%20and%20business%20growth%22%7D" className="text-lg text-text-secondary max-w-3xl mx-auto">
              Comprehensive technology solutions designed to drive digital transformation and business growth
            </p>
          </div>
          
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:457:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="457" data-component-end-line="471" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) =>
            <ServiceCard data-component-id="src/app/services/components/ServicesInteractive.tsx:459:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="459" data-component-end-line="468" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%7D"
              key={service.id}
              title={service.title}
              description={service.description}
              image={service.image}
              imageAlt={service.imageAlt}
              features={service.features}
              technologies={service.technologies}
              caseStudyCount={service.caseStudyCount}
              onExplore={() => handleExploreService(service.id)} />

            )}
          </div>
        </div>
      </section>

      <CapabilityMatrix data-component-id="src/app/services/components/ServicesInteractive.tsx:475:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="475" data-component-end-line="475" data-component-file="ServicesInteractive.tsx" data-component-name="CapabilityMatrix" data-component-content="%7B%22elementName%22%3A%22CapabilityMatrix%22%7D" data={capabilityData} />
      <TechnologyStack data-component-id="src/app/services/components/ServicesInteractive.tsx:476:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="476" data-component-end-line="476" data-component-file="ServicesInteractive.tsx" data-component-name="TechnologyStack" data-component-content="%7B%22elementName%22%3A%22TechnologyStack%22%7D" technologies={technologies} />
      <ServiceComparison data-component-id="src/app/services/components/ServicesInteractive.tsx:477:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="477" data-component-end-line="477" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceComparison" data-component-content="%7B%22elementName%22%3A%22ServiceComparison%22%7D" features={comparisonFeatures} />
      <CaseStudyIntegration data-component-id="src/app/services/components/ServicesInteractive.tsx:478:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="478" data-component-end-line="478" data-component-file="ServicesInteractive.tsx" data-component-name="CaseStudyIntegration" data-component-content="%7B%22elementName%22%3A%22CaseStudyIntegration%22%7D" caseStudies={caseStudies} />
      <ClientTestimonials data-component-id="src/app/services/components/ServicesInteractive.tsx:479:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="479" data-component-end-line="479" data-component-file="ServicesInteractive.tsx" data-component-name="ClientTestimonials" data-component-content="%7B%22elementName%22%3A%22ClientTestimonials%22%7D" testimonials={testimonials} />
      <ROICalculator data-component-id="src/app/services/components/ServicesInteractive.tsx:480:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="480" data-component-end-line="480" data-component-file="ServicesInteractive.tsx" data-component-name="ROICalculator" data-component-content="%7B%22elementName%22%3A%22ROICalculator%22%7D" />
      <CTASection data-component-id="src/app/services/components/ServicesInteractive.tsx:481:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="481" data-component-end-line="481" data-component-file="ServicesInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%7D" />
    </div>);

};

export default ServicesInteractive;